import React from 'react'
import 'project/polyfill'
import { Query } from '@prismicio/types'
import { ItemListElements } from 'next-seo/lib/types'
import { I18NConfig } from 'next/dist/server/config-shared'

import { Utils } from 'common/utils'
import Gallery from 'components/Gallery'
import { getPosts } from 'common/cms-sdk'
import Strings from 'project/localisation'
import { createClient } from '../prismicio'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { HoxtonMixLogo } from 'components/icons'
import imageResize from 'project/image-proxy-web'
import { TBlogPostResult } from 'common/cms-types'
import HeadingAndText from 'components/HeadingAndText'
import HotDeskCard from 'components/cards/HotDeskCard'
import BlogSection from 'components/sections/BlogSection'
import useWindowSize from 'components/util/useWindowSize'
import CoWorkingCard from 'components/cards/CoWorkingCard'
import AddressSection from 'components/sections/AddressSection'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import ReviewsTrustCard from 'components/cards/ReviewsTrustCard'
import PartnerHeroBanner from 'components/hero/PartnerHeroBanner'
import { membershipStrings } from 'common/strings/membership-strings'
import GetInTouchSection from 'components/sections/GetInTouchSection'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import MembershipFeaturesSection from 'components/sections/MembershipFeaturesSection'
import PrimeLondonBusinessAddressCard from 'components/cards/PrimeLondonBusinessAddressCard'

export type HomePageType = {
  posts: Query<TBlogPostResult>
}
const HomePage: NextPageWithLayout<HomePageType> = ({ posts }) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description: membershipStrings.seoMembershipDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/membership/',
    },
    title: membershipStrings.seoMembershipTitle,
  }

  const membershipBreadcrumb: ItemListElements[] = [
    homeBreadcrumb()[0],
    {
      item: 'https://www.hoxtonmix.com/membership/',
      name: `${Strings.membership}`,
      position: 2,
    },
  ]

  const windowSize = useWindowSize()

  return (
    <SeoContainer breadcrumb={membershipBreadcrumb} seoProps={seoProps}>
      <main className={'overflow-hidden'}>
        <PartnerHeroBanner
          partnershipLogo={<HoxtonMixLogo className='mb-6 mb-lg-4 max-w-100' />}
          title={membershipStrings.heroTitle}
          robotImage={{
            height: 506,
            src: '/images/new/robot-holding-mail.png',
            width: 392,
          }}
          imageContainerWidth={windowSize.isLgOrHigher ? '43%' : '100%'}
          description={membershipStrings.heroSubtitle}
        />
        <div className='container'>
          <HeadingAndText
            colSize={9}
            className={'mt-xxxl-38 mt-lg-19 mb-xxxl-80 mb-xl-54 mb-lg-25'}
            title={Strings.membership}
            subtitle={membershipStrings.membershipDescription}
          />
          <HotDeskCard imageOption={2} />
          <div className='row mt-lg-25 mb-6'>
            <CoWorkingCard />
            <ReviewsTrustCard option={2} color='purple1' />
          </div>
          <MembershipFeaturesSection />
          <HeadingAndText
            title={membershipStrings.pluginAndJoin}
            subtitle={membershipStrings.pluginAndJoinDescription}
            classesForSubTitle={'px-lg-0 px-2 mx-sm-0 mx-n1'}
            className={'mt-lg-13 mt-12'}
            colSize={12}
          />
        </div>
        <Gallery
          imageOption={2}
          images={[
            {
              img: imageResize(require('public/images/gallery/hot-desk5.jpg')),
              width: windowSize.isXxlOrHigher
                ? 421
                : windowSize.isXlOrHigher
                ? 321
                : 245,
            },
            {
              img: imageResize(require('public/images/gallery/hot-desk4.jpg')),
              width: windowSize.isXxlOrHigher
                ? 506
                : windowSize.isXlOrHigher
                ? 406
                : 197,
            },
            {
              img: imageResize(require('public/images/gallery/hot-desk2.jpg')),
              width: windowSize.isXxlOrHigher
                ? 451
                : windowSize.isXlOrHigher
                ? 351
                : 175,
            },
            {
              img: imageResize(require('public/images/gallery/hot-desk7.jpg')),
              width: windowSize.isXxlOrHigher
                ? 701
                : windowSize.isXlOrHigher
                ? 601
                : windowSize.isSmOrLower
                ? 272
                : 400,
            },
            {
              img: imageResize(require('public/images/gallery/hot-desk1.jpg')),
              width: windowSize.isXxlOrHigher
                ? 513
                : windowSize.isXlOrHigher
                ? 413
                : 199,
            },
            {
              img: imageResize(require('public/images/gallery/hot-desk6.jpg')),
              width: windowSize.isXxlOrHigher
                ? 632
                : windowSize.isXlOrHigher
                ? 532
                : 228,
            },
            {
              img: imageResize(require('public/images/gallery/hot-desk8.jpg')),
              width: windowSize.isXxlOrHigher
                ? 501
                : windowSize.isXlOrHigher
                ? 401
                : 238,
            },
            {
              img: imageResize(
                require('public/images/gallery/paul-street1.jpg'),
              ),
              width: windowSize.isXxlOrHigher
                ? 476
                : windowSize.isXlOrHigher
                ? 376
                : 335,
            },
          ]}
        />
        <div className='container'>
          <GetInTouchSection title={membershipStrings.membershipContactUs} />
          <div className={'mt-lg-30 mt-72'}>
            <PrimeLondonBusinessAddressCard />
            <AddressSection />
          </div>
        </div>
        <BlogSection posts={posts} />
        <div className={'spacer'} />
      </main>
    </SeoContainer>
  )
}
const PAGE_SIZE = 4

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)

    return {
      props: {
        posts,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}
export default HomePage
